import { Controller } from "@hotwired/stimulus"
import {
  getStorage,
  removeStorage,
  setStorage,
  lockSubmitButtons,
  unlockSubmitButtons,
} from "../instrument_take/index.js"

/**
 * Generic multi_step_capture archetype: local protocol state, storage draft, JSON commit on finish.
 *
 * Contract: `static targets` / `static values` and public actions must stay in sync with
 * `FormArchetypes::MultiStepCaptureFormComponent`.
 */
export default class extends Controller {
  static targets = [
    "live",
    "stepBadge",
    "cardLabel",
    "responsesList",
    "stimulusImage",
    "stimulusImageWrap",
    "stimulusPlaceholder",
    "rotateActions",
    "verbalAuxHint",
    "finalCardBox",
    "progress",
    "progressTrack",
    "progressFill",
    "verbalInput",
    "error",
    "nextButton",
    "finishButton",
  ]

  static values = {
    stimuli: Array,
    sessionId: String,
    cardCount: Number,
    rotatableThroughCard: { type: Number, default: 8 },
    rotateHint: String,
    fixedRotationHint: { type: String, default: "" },
    finishUrl: String,
    idleMs: { type: Number, default: 15000 },
    idleMessage: String,
    kindVerbal: String,
    kindDontKnow: String,
    kindSilence: String,
    kindRefusal: String,
    csrfToken: String,
    errorMessages: { type: Object, default: {} },
    responseLabels: { type: Object, default: {} },
    emptyResponsesMessage: String,
    cardProgressFormat: String,
    stepBadgeFormat: String,
    submittingLabel: String,
  }

  connect() {
    this._card = 1
    this._prevRenderedCard = null
    this._stimulusRotation = 0
    /** @type {Record<string, Array<{ kind: string, raw_text: string }>>} */
    this._cards = {}
    for (let i = 1; i <= this.cardCountValue; i += 1) {
      this._cards[String(i)] = []
    }

    this._loadDraft()
    this._renderAll()
    this._timer = window.setTimeout(() => this._remind(), this.idleMsValue)
  }

  disconnect() {
    window.clearTimeout(this._timer)
    if (this._transitionTimer) window.clearTimeout(this._transitionTimer)
  }

  addVerbal() {
    const text = (this.verbalInputTarget?.value || "").trim()
    if (!text) {
      this._flashError(this._msg("blank_verbal"))
      return
    }
    this._pushRow(this._card, { kind: this.kindVerbalValue, raw_text: text })
    this.verbalInputTarget.value = ""
    this._persist()
    this._renderResponses()
  }

  addDontKnow() {
    this._pushRow(this._card, { kind: this.kindDontKnowValue, raw_text: "" })
    this._persist()
    this._renderResponses()
  }

  addSilence() {
    this._pushRow(this._card, { kind: this.kindSilenceValue, raw_text: "" })
    this._persist()
    this._renderResponses()
  }

  addRefusal() {
    this._pushRow(this._card, { kind: this.kindRefusalValue, raw_text: "" })
    this._persist()
    this._renderResponses()
  }

  rotateStimulusCounterClockwise() {
    if (!this._stimulusRotationAllowed()) return
    this._stimulusRotation = (this._stimulusRotation - 90 + 360) % 360
    this._applyStimulusRotation()
  }

  rotateStimulusClockwise() {
    if (!this._stimulusRotationAllowed()) return
    this._stimulusRotation = (this._stimulusRotation + 90) % 360
    this._applyStimulusRotation()
  }

  nextCard() {
    if (this._card >= this.cardCountValue) return
    this._card += 1
    this._persist()
    this._renderAll()
  }

  async finish() {
    this._hideError()
    const visited_cards = Array.from({ length: this.cardCountValue }, (_, i) => i + 1)
    const cards = {}
    for (let i = 1; i <= this.cardCountValue; i += 1) {
      cards[String(i)] = this._cards[String(i)] || []
    }

    if (this._card !== this.cardCountValue) {
      this._flashError(this._msg("not_last_card"))
      return
    }

    try {
      lockSubmitButtons([this.finishButtonTarget], { label: this.submittingLabelValue })
      const res = await fetch(this.finishUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
        },
        body: JSON.stringify({
          visited_cards,
          cards,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.redirect_url) {
        removeStorage(this._storageKey())
        window.location.href = data.redirect_url
        return
      }
      this._flashError(data.error || this._msg("finish_failed"))
      unlockSubmitButtons([this.finishButtonTarget])
    } catch {
      this._flashError(this._msg("network_error"))
      unlockSubmitButtons([this.finishButtonTarget])
    }
  }

  _msg(key) {
    const m = this.errorMessagesValue || {}
    return m[key] || m.finish_failed || "Error"
  }

  _storageKey() {
    return `multi_step_capture_${this.sessionIdValue}`
  }

  _loadDraft() {
    const raw = getStorage(this._storageKey())
    if (!raw) return
    try {
      const draft = JSON.parse(raw)
      if (draft.currentCard >= 1 && draft.currentCard <= this.cardCountValue) {
        this._card = draft.currentCard
      }
      if (draft.cards && typeof draft.cards === "object") {
        for (let i = 1; i <= this.cardCountValue; i += 1) {
          const key = String(i)
          if (Array.isArray(draft.cards[key])) {
            this._cards[key] = draft.cards[key].map((row) => ({
              kind: String(row.kind),
              raw_text: row.raw_text != null ? String(row.raw_text) : "",
            }))
          }
        }
      }
    } catch {
      /* ignore corrupt draft */
    }
  }

  _persist() {
    setStorage(
      this._storageKey(),
      JSON.stringify({
        currentCard: this._card,
        cards: this._cards,
      })
    )
  }

  _pushRow(card, row) {
    const key = String(card)
    if (!this._cards[key]) this._cards[key] = []
    this._cards[key].push(row)
  }

  _renderAll() {
    this._renderStimulus()
    this._renderResponses()
    this._renderNav()
    this._renderProgress()
  }

  _renderProgress() {
    const current = String(this._card)
    const total = String(this.cardCountValue)
    const pct = `${((this._card / this.cardCountValue) * 100).toFixed(1)}%`

    if (this.hasProgressTarget) {
      this.progressTarget.value = current
      this.progressTarget.setAttribute("aria-valuenow", current)
    }
    if (this.hasProgressFillTarget) {
      this.progressFillTarget.style.width = pct
    }
    if (this.hasProgressTrackTarget) {
      this.progressTrackTarget.setAttribute("aria-valuenow", current)
      if (this.cardProgressFormatValue) {
        this.progressTrackTarget.setAttribute(
          "aria-label",
          this.cardProgressFormatValue.replace("___C___", current).replace("___T___", total)
        )
      }
    }
    if (this.hasCardLabelTarget && this.cardProgressFormatValue) {
      this.cardLabelTarget.textContent = this.cardProgressFormatValue
        .replace("___C___", current)
        .replace("___T___", total)
    }
    if (this.hasStepBadgeTarget && this.stepBadgeFormatValue) {
      this.stepBadgeTarget.textContent = this.stepBadgeFormatValue
        .replace("___C___", current)
        .replace("___T___", total)
    }
  }

  _renderStimulus() {
    const cardChanged = this._prevRenderedCard !== null && this._prevRenderedCard !== this._card
    this._prevRenderedCard = this._card

    const st = (this.stimuliValue || []).find((s) => Number(s.position) === this._card)
    const fixedDeg = this._fixedStimulusRotationDegrees(st)
    this._stimulusRotation = fixedDeg !== null ? fixedDeg : 0
    const isFinal = this._card === this.cardCountValue
    if (this.hasFinalCardBoxTarget) {
      this.finalCardBoxTarget.classList.toggle("is-hidden", !isFinal)
    }
    const url = st?.url
    const alt = st?.alt || ""

    if (this.hasStimulusImageTarget) {
      if (url) {
        this.stimulusImageTarget.src = url
        this.stimulusImageTarget.alt = alt
        this.stimulusImageTarget.classList.remove("is-hidden")
      } else {
        this.stimulusImageTarget.removeAttribute("src")
        this.stimulusImageTarget.classList.add("is-hidden")
      }
    }
    this._applyStimulusRotation()
    if (this.hasStimulusPlaceholderTarget) {
      this.stimulusPlaceholderTarget.textContent = url ? "" : alt
      this.stimulusPlaceholderTarget.classList.toggle("is-hidden", Boolean(url))
    }
    this._syncRotateUi(Boolean(url))

    if (cardChanged) this._pulseStimulusTransition()
  }

  _pulseStimulusTransition() {
    if (!this.hasStimulusImageWrapTarget) return
    const el = this.stimulusImageWrapTarget
    el.classList.remove("is-transitioning")
    void el.offsetWidth
    el.classList.add("is-transitioning")
    if (this._transitionTimer) window.clearTimeout(this._transitionTimer)
    this._transitionTimer = window.setTimeout(() => {
      el.classList.remove("is-transitioning")
    }, 350)
  }

  _applyStimulusRotation() {
    if (!this.hasStimulusImageTarget) return
    this.stimulusImageTarget.style.transform =
      this._stimulusRotation > 0 ? `rotate(${this._stimulusRotation}deg)` : ""
  }

  _stimulusEntry() {
    return (this.stimuliValue || []).find((s) => Number(s.position) === this._card)
  }

  /** @param {Record<string, unknown> | undefined} st */
  _manualRotationOff(st) {
    if (!st) return false
    return st.manual_rotation === false || st["manual_rotation"] === false
  }

  /** @param {Record<string, unknown> | undefined} st */
  _fixedStimulusRotationDegrees(st) {
    if (!st) return null
    const raw = st.fixed_rotation_degrees ?? st["fixed_rotation_degrees"]
    if (raw === undefined || raw === null || raw === "") return null
    const n = Number(raw)
    if (!Number.isFinite(n)) return null
    return ((n % 360) + 360) % 360
  }

  _stimulusRotationAllowed() {
    const st = this._stimulusEntry()
    if (!st?.url) return false
    if (this._manualRotationOff(st)) return false
    return this._card <= this.rotatableThroughCardValue
  }

  _syncRotateUi(hasImageUrl) {
    const st = this._stimulusEntry()
    const canRotate =
      Boolean(hasImageUrl) &&
      this._card <= this.rotatableThroughCardValue &&
      !this._manualRotationOff(st)
    const fixedHint =
      hasImageUrl && this._manualRotationOff(st) && this.fixedRotationHintValue
        ? this.fixedRotationHintValue
        : ""
    if (this.hasRotateActionsTarget) {
      this.rotateActionsTarget.classList.toggle("is-hidden", !canRotate)
    }
    if (this.hasVerbalAuxHintTarget) {
      const hint = canRotate && this.rotateHintValue ? this.rotateHintValue : fixedHint
      this.verbalAuxHintTarget.textContent = hint
      this.verbalAuxHintTarget.classList.toggle("is-hidden", !hint)
    }
  }

  _responseLabel(kind) {
    const labels = this.responseLabelsValue || {}
    return labels[kind] || kind
  }

  _responseTagClass(kind) {
    const safe = String(kind).replace(/[^a-z0-9_]/gi, "_")
    return `multi-step-capture-view__response-tag multi-step-capture-view__response-tag--${safe}`
  }

  _renderResponses() {
    if (!this.hasResponsesListTarget) return
    this.responsesListTarget.innerHTML = ""
    const rows = this._cards[String(this._card)] || []

    if (rows.length === 0) {
      const li = document.createElement("li")
      li.className = "multi-step-capture-view__response-row multi-step-capture-view__response-row--empty"
      li.textContent = this.emptyResponsesMessageValue || ""
      this.responsesListTarget.appendChild(li)
      return
    }

    rows.forEach((row) => {
      const li = document.createElement("li")
      li.className = "multi-step-capture-view__response-row"
      li.dataset.responseKind = row.kind

      const tag = document.createElement("span")
      tag.className = this._responseTagClass(row.kind)
      tag.textContent = this._responseLabel(row.kind)
      li.appendChild(tag)

      const text = document.createElement("span")
      text.className = "multi-step-capture-view__response-text"
      text.textContent = row.raw_text?.trim() ? row.raw_text : "—"
      li.appendChild(text)

      this.responsesListTarget.appendChild(li)
    })
  }

  _renderNav() {
    const last = this._card === this.cardCountValue
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("is-hidden", last)
      this.nextButtonTarget.disabled = last
    }
    if (this.hasFinishButtonTarget) {
      this.finishButtonTarget.classList.toggle("is-hidden", !last)
    }
  }

  _flashError(msg) {
    if (!this.hasErrorTarget) {
      window.alert(msg)
      return
    }
    this.errorTarget.textContent = msg
    this.errorTarget.classList.remove("is-hidden")
  }

  _hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("is-hidden")
    }
  }

  _remind() {
    if (!this.hasLiveTarget) return
    this.liveTarget.textContent = this.idleMessageValue || ""
  }
}
